import loadable from '@loadable/component';
import { useState, memo } from 'react';

import { usePortfolio } from '@/hooks/portfolio/usePortfolio';

import type { Holding, Portfolio } from '@/hooks/portfolio/usePortfolio';

import { Table, DemoTable } from '../../Table';
import { Rows } from './Rows';
import { RowSkeleton } from '../../HoldingsTableRow';

const RestrictionPlanModal = loadable(
  () => import('../../../../../components/RestrictionPlanModal'),
  {
    resolveComponent: (c) => c.RestrictionPlanModal,
  }
);

const PartiallyRestrictedObserver = loadable(
  () => import('./PartiallyRestrictedObserver'),
  {
    resolveComponent: (c) => c.PartiallyRestrictedObserver,
  }
);

const HoldingRowExpanded = loadable(() => import('../../HoldingsTableRow'), {
  resolveComponent: (c) => c.HoldingRowExpanded,
});

const DemoHoldingRowExpanded = loadable(
  () => import('../../HoldingsTableRow'),
  {
    resolveComponent: (c) => c.DemoHoldingRowExpanded,
  }
);

const ExcludedHoldingRows = loadable(
  () => import('./TableExcludedHoldingRows'),
  { resolveComponent: (c) => c.TableExcludedHoldingRows }
);

const HoldingRow = memo(
  loadable(() => import('../../HoldingsTableRow'), {
    resolveComponent: (c) => c.HoldingRow,
    fallback: <RowSkeleton />,
  })
);

const DemoHoldingRow = memo(
  loadable(() => import('../../HoldingsTableRow'), {
    resolveComponent: (c) => c.DemoHoldingRow,
    fallback: <RowSkeleton />,
  })
);

export const LoadingState = () => {
  return (
    <>
      <RowSkeleton />
      <RowSkeleton />
      <RowSkeleton />
      <RowSkeleton />
      <RowSkeleton />
    </>
  );
};

type Props = {
  portfolioId: Portfolio['id'];
  holdingId: Holding['id'] | null;
  onHoldingSelect: (id: Holding['id'] | null) => void;
  holdings: Holding[];
  excludedHoldings: Holding[];
};

export const HoldingsTable = ({
  portfolioId,
  onHoldingSelect,
  holdingId,
  holdings,
  excludedHoldings,
}: Props) => {
  const { data: portfolio, status } = usePortfolio({ id: portfolioId });

  const [showRestrictionPlanModal, setShowRestrictionPlanModal] =
    useState(false);

  if (status === 'pending') {
    return (
      <Table holdingId={holdingId}>
        <LoadingState />
      </Table>
    );
  }

  if (status === 'success' && typeof portfolio !== 'undefined') {
    return (
      <>
        <Table holdingId={holdingId}>
          <Rows
            portfolio={portfolio}
            holdings={holdings}
            holdingId={holdingId}
            onHoldingSelect={onHoldingSelect}
            row={HoldingRow}
            rowExpanded={HoldingRowExpanded}
          />
          {excludedHoldings.length > 0 && (
            <>
              {!portfolio.isAnalysisRestricted && (
                <PartiallyRestrictedObserver
                  handleIntersecting={() => setShowRestrictionPlanModal(true)}
                />
              )}
              <ExcludedHoldingRows
                portfolio={portfolio}
                excludedHoldings={excludedHoldings}
                holdingId={holdingId}
                onHoldingSelect={onHoldingSelect}
              />
            </>
          )}
        </Table>
        {showRestrictionPlanModal && !portfolio.isAnalysisRestricted && (
          <RestrictionPlanModal portfolio={portfolio} limitedType="partially" />
        )}
      </>
    );
  }

  return null;
};

export const DemoHoldingsTable = ({
  portfolioId,
  onHoldingSelect,
  holdingId,
  holdings,
  excludedHoldings,
}: Props) => {
  const { data: portfolio, status } = usePortfolio({ id: portfolioId });

  if (status === 'pending') {
    return (
      <DemoTable holdingId={holdingId}>
        <LoadingState />
      </DemoTable>
    );
  }

  if (status === 'success' && typeof portfolio !== 'undefined') {
    return (
      <DemoTable holdingId={holdingId}>
        <Rows
          portfolio={portfolio}
          holdings={holdings}
          holdingId={holdingId}
          onHoldingSelect={onHoldingSelect}
          row={DemoHoldingRow}
          rowExpanded={DemoHoldingRowExpanded}
        />
        {excludedHoldings.length > 0 && (
          <ExcludedHoldingRows
            portfolio={portfolio}
            excludedHoldings={excludedHoldings}
            holdingId={holdingId}
            onHoldingSelect={onHoldingSelect}
          />
        )}
      </DemoTable>
    );
  }

  return null;
};
